import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import style from "./SelectField.module.scss";

/**
 * SelectField — the primary single-select dropdown primitive for the app.
 *
 * This is the recommended select for all new forms, alongside `InputField`. It replaces the
 * browser-native `<select>` popup (which can't be restyled beyond its closed control) with a
 * fully custom, keyboard-navigable listbox built from our own SCSS — no native popup ceiling,
 * no new dependency.
 *
 * `SelectBox` (`src/components/primary/SelectBox`) predates this component and is deprecated;
 * it remains only to avoid regressing its existing usages until they're migrated.
 *
 * RHF compatibility note: because the visible control is a custom `button` + `listbox` (not a
 * native form element), a visually hidden native `<select>` is kept in sync under the hood so
 * this component still participates in the DOM the way RHF expects:
 *   - RHF v6 style: pass `inputRef={register(rules)}` — forwarded to the hidden `<select>`'s
 *     ref, and its native `input`/`change` events are dispatched on every selection so v6's
 *     internally-attached listeners pick it up.
 *   - RHF v7 style: pass `register={register(name)}` — its `ref` is forwarded the same way,
 *     and its `onChange`/`onBlur` are invoked directly with a synthetic `{ target: { name, value } }`
 *     event on every selection.
 *   - `<Controller>` style: use the plain `value` / `onChange` / `onBlur` controlled props.
 */

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const mergeRefs =
  (...refs) =>
  (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else ref.current = node;
    });
  };

export const SelectField = ({
  id,
  name,
  label,
  options = [],
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  placeholder = "Select…",
  disabled = false,
  required = false,
  searchable = false,
  error,
  helperText,
  hint,
  className = "",
  classNames = {},
  register,
  inputRef,
  onClear,
  dataTestId,
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const currentValue = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const hiddenSelectRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((option) => String(option.value).toLowerCase().includes(term));
  }, [options, searchable, searchTerm]);

  const selectedOption = options.find((option) => option.key === currentValue);

  const errorId = error && id ? `${id}-error` : undefined;
  const helperId = (helperText || hint) && id ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(" ") || undefined;
  const helperMessage = helperText || hint;
  const labelId = id ? `${id}-label` : undefined;
  const menuId = id ? `${id}-listbox` : undefined;

  useEffect(() => {
    const node = hiddenSelectRef.current;
    if (!node) return;
    const nextValue = currentValue ?? "";
    if (node.value !== nextValue) {
      node.value = nextValue;
      node.dispatchEvent(new Event("input", { bubbles: true }));
      node.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }, [currentValue]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Before the menu has ever rendered there's nothing to measure, so estimate from the
      // option count (capped at the CSS max-height of 260px) instead of always assuming the
      // worst case - a short list shouldn't flip the menu upward unnecessarily. Once it has
      // rendered, prefer the real measured height.
      const estimatedHeight = Math.min(260, filteredOptions.length * 34 + (searchable ? 40 : 0) + 8);
      const menuHeight = menuRef.current?.offsetHeight || estimatedHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow;

      setMenuPosition(
        openUpward
          ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, openUpward: true }
          : { top: rect.bottom + 4, left: rect.left, width: rect.width, openUpward: false }
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideRoot && !insideMenu) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setSearchTerm("");
  };

  const getInitialHighlight = () => {
    const idx = filteredOptions.findIndex((option) => option.key === currentValue);
    return idx >= 0 ? idx : 0;
  };

  const notify = (nextValue) => {
    if (!isControlled) setInternalValue(nextValue);
    const syntheticEvent = { target: { name, value: nextValue }, type: "change" };
    onChange && onChange(syntheticEvent);
    register?.onChange && register.onChange(syntheticEvent);
  };

  const selectOption = (option) => {
    if (option.disabled) return;
    notify(option.key);
    closeMenu();
    buttonRef.current?.focus();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    notify("");
    onClear && onClear();
  };

  const handleButtonKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightedIndex(getInitialHighlight());
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setHighlightedIndex(getInitialHighlight());
        } else {
          setHighlightedIndex((i) => Math.max(i - 1, 0));
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (open) {
          const option = filteredOptions[highlightedIndex];
          if (option) selectOption(option);
        } else {
          setOpen(true);
          setHighlightedIndex(getInitialHighlight());
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          closeMenu();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className={cx(style.root, className, classNames.root)}>
      {label && (
        <label id={labelId} htmlFor={id} className={cx(style.label, classNames.label)} onClick={(e) => e.preventDefault()}>
          {label} {required ? <span className={style.astrick}>&#42;</span> : ""}
        </label>
      )}

      <button
        type="button"
        id={id}
        ref={buttonRef}
        className={cx(style.control, error && style.controlError, disabled && style.controlDisabled, open && style.controlOpen, classNames.control)}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-labelledby={label ? labelId : undefined}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleButtonKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        data-testid={dataTestId || id}
      >
        <span className={cx(style.controlValue, !selectedOption && style.placeholder)}>{selectedOption ? selectedOption.value : placeholder}</span>

        {onClear && selectedOption && !disabled && (
          <span className={style.clearIcon} data-testid="clear_icon" onClick={handleClear}>
            <i className="fa fa-times" aria-hidden="true"></i>
          </span>
        )}

        <span className={cx(style.chevron, open && style.chevronOpen)} aria-hidden="true" />
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <ul
            id={menuId}
            ref={menuRef}
            className={cx(style.menu, classNames.menu)}
            role="listbox"
            aria-labelledby={labelId}
            style={{
              position: "fixed",
              ...(menuPosition.openUpward ? { bottom: menuPosition.bottom } : { top: menuPosition.top }),
              left: menuPosition.left,
              width: menuPosition.width,
              right: "auto",
            }}
          >
            {searchable && (
              <li className={style.searchRow}>
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <input
                  type="text"
                  autoFocus
                  className={style.searchInput}
                  placeholder="Search…"
                  value={searchTerm}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  data-testid={id ? `${id}-search` : "select-search"}
                />
              </li>
            )}

            {filteredOptions.length === 0 && <li className={style.emptyState}>No options</li>}

            {filteredOptions.map((option, index) => (
              <li
                key={option.key}
                role="option"
                aria-selected={option.key === currentValue}
                aria-disabled={!!option.disabled}
                className={cx(
                  style.option,
                  option.key === currentValue && style.optionSelected,
                  option.disabled && style.optionDisabled,
                  index === highlightedIndex && style.optionHighlighted,
                  classNames.option
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option)}
                data-testid={id ? `${id}-option-${option.key}` : `select-option-${option.key}`}
              >
                {option.value}
              </li>
            ))}
          </ul>,
          document.body
        )}

      <select
        ref={mergeRefs(hiddenSelectRef, inputRef, register?.ref)}
        name={name}
        tabIndex={-1}
        aria-hidden="true"
        className={style.hiddenSelect}
        defaultValue={currentValue}
        onChange={() => {}}
        onBlur={register?.onBlur}
      >
        <option value=""></option>
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.value}
          </option>
        ))}
      </select>

      {error && (
        <span id={errorId} className={cx(style.errorText, classNames.error)}>
          {error}
        </span>
      )}

      {!error && helperMessage && (
        <span id={helperId} className={cx(style.helperText, classNames.helperText)}>
          {helperMessage}
        </span>
      )}
    </div>
  );
};

SelectField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.node,
  hint: PropTypes.node,
  className: PropTypes.string,
  classNames: PropTypes.shape({
    root: PropTypes.string,
    label: PropTypes.string,
    control: PropTypes.string,
    menu: PropTypes.string,
    option: PropTypes.string,
    error: PropTypes.string,
    helperText: PropTypes.string,
  }),
  register: PropTypes.object,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onClear: PropTypes.func,
  dataTestId: PropTypes.string,
};
